/**
 * The Documents 'Inbox & Extraction' rows, read from real intake records.
 *
 * Stress test MOD-20: the tab's only data source was the demo fixture, so on a
 * live account it read 'Received 0 · Parsing 0 · Extracted 0 · Validated 0 ·
 * Filed 0 … Forward an email or upload a file and it will appear here' beside
 * a Vault holding 70 filed intake documents and an Intake screen with 44
 * records. Every document intake received is here now, in the stage the
 * record it arrived with has actually reached.
 */
import type { Ingestion } from '@/services/ingestion'
import type { DocumentFileType, IncomingDocument, IngestionStatus } from '@/types/documentIngestion'

function fileType(filename: string): DocumentFileType {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'xlsx' || ext === 'xlsm' || ext === 'xls') return 'xlsx'
  if (ext === 'docx' || ext === 'doc') return 'docx'
  if (ext === 'csv') return 'csv'
  return 'pdf'
}

function sizeText(kb: number | null | undefined): string {
  if (typeof kb !== 'number' || !Number.isFinite(kb)) return '—'
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`
}

/** The stage a document has reached is the stage its record has reached.
 *  Parsing is synchronous at intake, so nothing is ever caught mid-parse. */
function stage(record: Ingestion, parsed: boolean | undefined): IngestionStatus {
  if (record.status === 'converted' && record.convertedToLoanId) return 'filed'
  if (parsed === false) return 'received'
  if (record.status === 'reviewed') return 'validated'
  return 'extracted'
}

function extracted(record: Ingestion): Record<string, string | number> | undefined {
  const out: Record<string, string | number> = {}
  if (record.extractedPropertyName) out['Property'] = record.extractedPropertyName
  if (typeof record.extractedLoanAmount === 'number' && record.extractedLoanAmount > 0) {
    out['Loan amount'] = `$${(record.extractedLoanAmount / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`
  }
  if (record.extractedSponsor) out['Sponsor / Borrower'] = record.extractedSponsor
  if (record.extractedLocation) out['Location'] = record.extractedLocation
  if (record.extractedAssetType) out['Asset type'] = record.extractedAssetType
  return Object.keys(out).length ? out : undefined
}

export function intakeRecordsToDocuments(records: Ingestion[]): IncomingDocument[] {
  const rows: IncomingDocument[] = []
  for (const record of records) {
    const email = record.sourceType === 'email'
    const envelope = record.emailEnvelope
    const base = {
      source: email ? ('email' as const) : ('upload' as const),
      senderEmail: envelope?.fromEmail ?? record.sourceEmail ?? undefined,
      senderName: envelope?.fromName ?? record.brokerName ?? undefined,
      subject: record.sourceSubject ?? undefined,
      receivedAt: record.receivedAt ?? record.createdAt ?? new Date(0).toISOString(),
      dealId: record.convertedToLoanId ?? undefined,
      extractedFields: extracted(record),
      // Field completeness — what the engine could read — never a model's
      // confidence; the column says so.
      confidence: typeof record.extractionConfidence === 'number' ? record.extractionConfidence : undefined,
      ingestionId: record.id,
    }
    const attachments = record.attachments ?? []
    if (attachments.length === 0) {
      rows.push({
        ...base,
        id: `${record.id}:source`,
        fileName: record.rawFilePaths?.[0]?.filename ?? `${record.sourceSubject || record.id}`,
        fileType: fileType(record.rawFilePaths?.[0]?.filename ?? ''),
        fileSize: '—',
        status: stage(record, true),
      })
      continue
    }
    attachments.forEach((a, i) => {
      rows.push({
        ...base,
        id: `${record.id}:${a.sha256 ?? i}`,
        fileName: a.filename,
        fileType: fileType(a.filename),
        fileSize: sizeText(a.sizeKb),
        status: stage(record, a.parsed),
      })
    })
  }
  return rows.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
}
